import React, { useCallback, useMemo, useRef } from 'react';
import { di } from 'react-magnetic-di';
import { ExtensionPointProvider } from 'view/common/forge-ui/extension-point-provider';
import { ExtensionProvider } from 'view/common/forge-ui/extension-provider';
import { Renderer } from 'view/common/forge-ui/renderer';
import type { ComponentMap } from '@atlassian/forge-ui';
import type { BridgeConfig } from '@atlassian/forge-ui/dist/types/custom-ui/bridge';
import type { RenderFn } from '@atlassian/forge-ui-types';

import { useForgeCustomFieldStateSync } from '../controllers/store/forge-custom-field-state-sync';
import { CustomFieldEdit } from '../forge-custom-field-uikit-edit';
import {
    ContextTokenType,
    type CustomField,
    type CustomFieldExtensionData,
    type CustomFieldExtensionPayload,
    type CustomFieldType,
} from 'view/common/forge-ui/types';

interface Props {
    extension: CustomField | CustomFieldType;
    extensionData: CustomFieldExtensionData;
    extensionPayload: CustomFieldExtensionPayload;
    loadingComponent: React.ReactNode;
    onSubmit: (fieldValue: unknown) => void;
}

type RenderFnProps = Parameters<RenderFn>[0];

export const ForgeCustomFieldEdit = ({
    extension,
    extensionData,
    extensionPayload,
    loadingComponent,
    onSubmit,
}: Props) => {
    di(ExtensionPointProvider, ExtensionProvider, Renderer);

    const containerRef = useRef<HTMLDivElement>(null);

    const { submit } = useForgeCustomFieldStateSync({ onSubmit, containerRef, fieldId: extensionData.fieldId });

    const getComponents = useCallback(
        (defaults: ComponentMap) => ({
            ...defaults,
            CustomFieldEdit: (props: RenderFnProps) => <CustomFieldEdit {...props} fieldId={extensionData.fieldId} />,
        }),
        [extensionData.fieldId]
    );

    const bridge = useMemo(
        () => ({
            // The typing of the submit method is not correct in the Forge UI types.
            // It works with promises, but it is not reflected in there.
            submit: submit as unknown as BridgeConfig['submit'],
        }),
        [submit]
    );

    return (
        <ExtensionPointProvider extensionType={extension.type} noWrapper>
            <ExtensionProvider extension={extension} noWrapper>
                <div ref={containerRef}>
                    <Renderer
                        extension={extension}
                        extensionData={extensionData}
                        extensionPayload={extensionPayload}
                        getComponents={getComponents}
                        entryPoint="edit"
                        bridge={bridge}
                        loadingComponent={loadingComponent as JSX.Element}
                        contextTokenType={ContextTokenType.JIRA}
                    />
                </div>
            </ExtensionProvider>
        </ExtensionPointProvider>
    );
};
